import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { API_URL, fetchAPI } from "../lib/utils";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function InviteOnboarding() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { user, loading } = useAuth();
	const { t } = useLanguage();

	const token = (searchParams.get("token") || "").trim();
	const [preview, setPreview] = useState(null);
	const [previewLoading, setPreviewLoading] = useState(true);
	const [previewError, setPreviewError] = useState("");
	const [applying, setApplying] = useState(false);

	const handleApplyInvite = useCallback(async () => {
		if (!token) return;
		setApplying(true);
		setPreviewError("");
		try {
			await fetchAPI(`/invites/${encodeURIComponent(token)}/accept`, {
				method: "POST",
			});
			localStorage.removeItem("pending_invite_token");
			toast.success(t("inviteAcceptedSuccess"));
			navigate("/dashboard", { replace: true });
		} catch (error) {
			setPreviewError(error.message || t("inviteAcceptFailed"));
			toast.error(error.message || t("inviteAcceptFailed"));
		} finally {
			setApplying(false);
		}
	}, [navigate, t, token]);

	useEffect(() => {
		if (!token) {
			setPreviewLoading(false);
			setPreviewError(t("inviteInvalid"));
			return;
		}

		localStorage.setItem("pending_invite_token", token);

		let cancelled = false;
		const loadPreview = async () => {
			setPreviewLoading(true);
			setPreviewError("");
			try {
				const response = await fetch(
					`${API_URL}/api/invites/${encodeURIComponent(token)}/preview`,
					{ credentials: "include" },
				);
				const data = await response.json().catch(() => ({}));
				if (!response.ok) {
					throw new Error(data.detail || t("inviteInvalid"));
				}
				if (!cancelled) setPreview(data);
			} catch (error) {
				if (!cancelled) setPreviewError(error.message || t("inviteInvalid"));
			} finally {
				if (!cancelled) setPreviewLoading(false);
			}
		};

		loadPreview();

		return () => {
			cancelled = true;
		};
	}, [token, t]);

	useEffect(() => {
		if (loading || !user || !token || !preview || previewError || applying)
			return;
		handleApplyInvite();
	}, [
		applying,
		handleApplyInvite,
		loading,
		preview,
		previewError,
		token,
		user,
	]);

	const handleGoToLogin = () => {
		if (token) {
			localStorage.setItem("pending_invite_token", token);
			navigate(`/login?invite_token=${encodeURIComponent(token)}`);
			return;
		}
		navigate("/login");
	};

	const handleGoToPortal = () => {
		if (user) {
			navigate("/dashboard");
			return;
		}
		navigate("/login");
	};

	return (
		<div className="theme-dark-scope min-h-screen bg-obsidian flex items-center justify-center px-4">
			<Card className="w-full max-w-lg bg-card border-border rounded-sm">
				<CardHeader>
					<CardTitle className="font-heading text-2xl">
						{t("inviteTitle")}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					{previewLoading ? (
						<p className="text-sm text-muted-foreground">
							{t("inviteLoading")}
						</p>
					) : previewError ? (
						<div className="space-y-3">
							<p className="text-sm text-blaze">{previewError}</p>
							<p className="text-sm text-muted-foreground">
								{t("errorLinkClick")}
							</p>
							<Button
								onClick={handleGoToPortal}
								variant="outline"
								className="w-full rounded-sm border-border"
							>
								{t("goToPortal")}
							</Button>
						</div>
					) : (
						<>
							<p className="text-sm text-muted-foreground">
								{t("inviteSubtitle")}
							</p>
							<div className="space-y-2 text-sm">
								<p>
									<strong>{t("inviteCoachLabel")}:</strong>{" "}
									{preview?.coach?.coach_name || "-"}
								</p>
								{preview?.group ? (
									<p>
										<strong>{t("inviteGroupLabel")}:</strong>{" "}
										{preview.group.group_name || "-"}
									</p>
								) : null}
								{preview?.program ? (
									<p>
										<strong>{t("inviteProgramLabel")}:</strong>{" "}
										{preview.program.program_title || "-"}
									</p>
								) : null}
							</div>

							{user ? (
								<Button
									onClick={handleApplyInvite}
									className="w-full bg-volt hover:bg-volt/90 rounded-sm"
									disabled={applying}
								>
									{applying ? t("inviteApplying") : t("acceptInviteNow")}
								</Button>
							) : (
								<Button
									onClick={handleGoToLogin}
									className="w-full bg-volt hover:bg-volt/90 rounded-sm"
								>
									{t("goToLoginToAccept")}
								</Button>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
